import { AccountWithdrawalDataRecord, WithdrawalTypeEnum } from '@tassat/fred-definitions';
import { ActionTypes } from '@tassat/ui-definitions';

import { IErrors, IFormValues } from './types';
import {
    textFieldCharacterWhitelist,
    validateAccountNumberFormatting,
    validateCapitalLetters,
    validateDDALengthRequirement,
    validateLengthRequirement,
    validateNoExcelFormulaStartingCharacters,
    validateNoPoBox
} from '../helpers/validationHelpers';

export const validateWithdrawalForm = async (
    values: IFormValues,
    opts: {
        errors: IErrors;
        editWithdrawalInstruction?: AccountWithdrawalDataRecord;
        firmWithdrawalInstructions: Array<AccountWithdrawalDataRecord>;
        actionType: ActionTypes;
        arePendingTransactions?: (instructionId: string) => Promise<boolean>;
    }
) => {
    const {
        errors,
        editWithdrawalInstruction,
        firmWithdrawalInstructions,
        actionType,
        arePendingTransactions
    } = opts;

    const description = values.Description.trim();
    const withdrawalDescriptionExcelError = validateNoExcelFormulaStartingCharacters(description);

    if (!description) errors.Description = 'Required';
    else if (withdrawalDescriptionExcelError) {
        errors.Description = withdrawalDescriptionExcelError;
    }

    const withdrawalDescriptionWhitelistCheckError = textFieldCharacterWhitelist(description);
    if (withdrawalDescriptionWhitelistCheckError) errors.Description = withdrawalDescriptionWhitelistCheckError;

    if ((actionType === ActionTypes.add) ||
        (actionType === ActionTypes.edit && description !== editWithdrawalInstruction?.Description)) {
        const isDuplicate = firmWithdrawalInstructions.some((instruction) => instruction.Description === description);
        if (isDuplicate) errors.Description = 'Cannot have duplicate withdrawal description';
    }

    if (values.WithdrawalType.value === WithdrawalTypeEnum.BLOCKCHAIN) {
        validateBlockchainWithdrawal(values, errors);
    } else {
        validateBankBasedWithdrawal(values, errors);
    }

    if (actionType === ActionTypes.edit && editWithdrawalInstruction &&
        typeof arePendingTransactions === 'function' && Object.keys(errors).length === 0) {
        try {
            const arePending = await arePendingTransactions(editWithdrawalInstruction.AccountWithdrawalDataId);
            if (arePending) {
                errors.Description = 'Cannot edit Withdrawal Instruction with pending withdrawals';
            }
        } catch (error) {
            errors.Description = 'Cannot validate if Withdrawal Instruction has pending withdrawals';
        }
    }

    return errors;
};

const validateBlockchainWithdrawal = (values: IFormValues, errors: IErrors): void => {
    if (!values.Blockchain?.value) errors.Blockchain = 'Required';

    const address = values.Address.trim();
    const addressWhitelistError = textFieldCharacterWhitelist(address);
    const addressExcelError = validateNoExcelFormulaStartingCharacters(address);
    if (!address) errors.Address = 'Required';
    else if (addressWhitelistError) errors.Address = addressWhitelistError;
    else if (addressExcelError) errors.Address = addressExcelError;
};

const validateBankBasedWithdrawal = (values: IFormValues, errors: IErrors): void => {
    // INFO: Beneficiary Fields
    const beneName = values.BeneName.trim();
    const beneAcct = values.BeneBankAcct.trim();
    const beneStreet = values.BeneStreetAddress.trim();
    const beneCity = values.BeneCity.trim();
    const beneStateProv = typeof values.BeneStateProv === 'object' ?
        values.BeneStateProv.value.toString()
        : values.BeneStateProv.trim();
    const benePostalCode = values.BenePostalCode.trim();
    const beneCountry = values.BeneCountry.trim();

    const beneNameWhitelistError = textFieldCharacterWhitelist(beneName);
    const beneNameExcelError = validateNoExcelFormulaStartingCharacters(beneName);
    if (!beneName) errors.BeneName = 'Required';
    else if (beneNameWhitelistError) errors.BeneName = beneNameWhitelistError;
    else if (beneNameExcelError) errors.BeneName = beneNameExcelError;

    const beneAddress: AddressFields = {
        streetAddress: {
            fieldName: 'BeneStreetAddress',
            value: beneStreet
        },
        city: {
            fieldName: 'BeneCity',
            value: beneCity
        },
        stateProv: {
            fieldName: 'BeneStateProv',
            value: beneStateProv
        },
        postalCode: {
            fieldName: 'BenePostalCode',
            value: benePostalCode
        },
        countryCode: {
            fieldName: 'BeneCountry',
            value: beneCountry
        }
    };
    validateAddress(beneAddress, errors, beneCountry === 'US');

    // INFO: Beneficiary Bank Fields
    const beneBankName = values.BeneBankName.trim();
    const beneBankAccountType = values.BeneBankAccountType.value;
    const beneBankAba = values.BeneBankRoutNum;
    const beneBankSwift = values.BeneBankBic;
    const beneBankStreet = values.BeneBankStreetAddress.trim();
    const beneBankCity = values.BeneBankCity.trim();
    const beneBankStateProv = typeof values.BeneBankStateProv === 'object' ?
        values.BeneBankStateProv.value.toString()
        : values.BeneBankStateProv.trim();
    const beneBankPostalCode = values.BeneBankPostalCode.trim();
    const beneBankCountry = values.BeneBankCountry.trim();

    const beneBankNameWhitelistError = textFieldCharacterWhitelist(beneBankName);
    const beneBankNameExcelError = validateNoExcelFormulaStartingCharacters(beneBankName);
    if (!beneBankName) errors.BeneBankName = 'Required';
    else if (beneBankNameWhitelistError) errors.BeneBankName = beneBankNameWhitelistError;
    else if (beneBankNameExcelError) errors.BeneBankName = beneBankNameExcelError;

    if (!beneBankAccountType) errors.BeneBankAccountType = 'Required';

    const beneBankAddress: AddressFields = {
        streetAddress: {
            fieldName: 'BeneBankStreetAddress',
            value: beneBankStreet
        },
        city: {
            fieldName: 'BeneBankCity',
            value: beneBankCity
        },
        stateProv: {
            fieldName: 'BeneBankStateProv',
            value: beneBankStateProv
        },
        postalCode: {
            fieldName: 'BeneBankPostalCode',
            value: beneBankPostalCode
        },
        countryCode: {
            fieldName: 'BeneBankCountry',
            value: beneBankCountry
        }
    };
    validateAddress(beneBankAddress, errors, values.WithdrawalType.value === WithdrawalTypeEnum.US);

    // INFO: Intermediary Bank Fields
    const intBankName = values.IntermediaryBankName.trim();
    const intBankAba = values.IntermediaryBankRoutNum.trim();
    const intBankSwift = values.IntermediaryBankBic.trim();
    const intBankStreet = values.IntermediaryBankStreetAddress.trim();
    const intBankCity = values.IntermediaryBankCity.trim();
    const intBankStateProv = typeof values.IntermediaryBankStateProv === 'object' ?
        values.IntermediaryBankStateProv.value.toString()
        : values.IntermediaryBankStateProv.trim();
    const intBankPostalCode = values.IntermediaryBankPostalCode.trim();
    const intBankCountry = values.IntermediaryBankCountry.trim();

    // INFO: If ANY Fields for Intemediary Bank are populated, run validation
    if (
        intBankName || intBankAba || intBankSwift ||
        intBankStreet || intBankCity || intBankStateProv ||
        intBankPostalCode || intBankCountry
    ) {
        const intBankAddress: AddressFields = {
            streetAddress: {
                fieldName: 'IntermediaryBankStreetAddress',
                value: intBankStreet
            },
            city: {
                fieldName: 'IntermediaryBankCity',
                value: intBankCity
            },
            stateProv: {
                fieldName: 'IntermediaryBankStateProv',
                value: intBankStateProv
            },
            postalCode: {
                fieldName: 'IntermediaryBankPostalCode',
                value: intBankPostalCode
            },
            countryCode: {
                fieldName: 'IntermediaryBankCountry',
                value: intBankCountry
            }
        };
        validateAddress(intBankAddress, errors, intBankCountry === 'US');

        const intBankNameWhitelistError = textFieldCharacterWhitelist(intBankName);
        const intBankNameExcelError = validateNoExcelFormulaStartingCharacters(intBankName);
        if (!intBankName) errors.IntermediaryBankName = 'Required';
        else if (intBankNameWhitelistError) errors.IntermediaryBankName = intBankNameWhitelistError;
        else if (intBankNameExcelError) errors.IntermediaryBankName = intBankNameExcelError;

        if (!intBankAba && !intBankSwift) {
            errors.IntermediaryBankRoutNum = 'Swift BIC or ABA Number Required';
            errors.IntermediaryBankBic = 'Swift BIC or ABA Number Required';
        }

        if (intBankAba) {
            const intBankAbaLengthError = validateDDALengthRequirement(intBankAba, {
                ddaLengthMax: 9,
                ddaLengthMin: 9
            });
            const intBankAbaFormatError = validateAccountNumberFormatting(intBankAba, 'Intermediary Bank ABA Number', { allowLeadingZeros: true });
            if (intBankAbaLengthError) errors.IntermediaryBankRoutNum = intBankAbaLengthError;
            else if (intBankAbaFormatError) errors.IntermediaryBankRoutNum = intBankAbaFormatError;
        }

        if (intBankSwift) {
            const intBankSwifthLength8Error = validateLengthRequirement(intBankSwift, 8, true);
            const intBankSwifthLength11Error = validateLengthRequirement(intBankSwift, 11, true);
            const intBankSwitchFormatError = validateAccountNumberFormatting(
                intBankSwift,
                'Intermediary Bank SWIFT BIC',
                {
                    allowAlphanumericCapital: true,
                    allowLeadingZeros: true
                }
            );
            if (intBankSwifthLength8Error && intBankSwifthLength11Error) errors.IntermediaryBankBic = 'Field must be exactly 8 or 11 characters';
            else if (intBankSwitchFormatError) errors.IntermediaryBankBic = intBankSwitchFormatError;
        }
    }

    if (values.WithdrawalType.value === 'U') {
        // INFO: Domestic Wire validations
        const beneAcctFormatError = validateAccountNumberFormatting(beneAcct, 'Beneficiary Account', { allowLeadingZeros: true });
        const beneAcctLengthError = validateDDALengthRequirement(beneAcct, { ddaLengthMin: 6 });
        if (beneAcctFormatError) errors.BeneBankAcct = beneAcctFormatError;
        else if (beneAcctLengthError) errors.BeneBankAcct = beneAcctLengthError;

        const beneBankAbaLengthError = validateDDALengthRequirement(beneBankAba, {
            ddaLengthMax: 9,
            ddaLengthMin: 9
        });
        const beneBankAbaFormatError = validateAccountNumberFormatting(beneBankAba, 'Beneficiary Bank ABA Number', { allowLeadingZeros: true });
        if (!beneBankAba) errors.BeneBankRoutNum = 'Required';
        else if (beneBankAbaLengthError) errors.BeneBankRoutNum = beneBankAbaLengthError;
        else if (beneBankAbaFormatError) errors.BeneBankRoutNum = beneBankAbaFormatError;
    } else if (values.WithdrawalType.value === 'I') {
        // INFO: Internation Wire validations
        const beneAcctLengthError = validateDDALengthRequirement(beneAcct, { ddaLengthMin: 6 });
        if (beneAcctLengthError) errors.BeneBankAcct = beneAcctLengthError;
        const beneAcctFormatError = validateAccountNumberFormatting(
            beneAcct,
            'Beneficiary Account',
            {
                allowAlphanumeric: true,
                allowLeadingZeros: true
            }
        );
        if (beneAcctFormatError) errors.BeneBankAcct = beneAcctFormatError;

        const beneBankSwiftLength8Error = validateLengthRequirement(beneBankSwift, 8, true);
        const beneBankSwiftLength11Error = validateLengthRequirement(beneBankSwift, 11, true);
        const beneBankSwiftFormatError = validateAccountNumberFormatting(
            beneBankSwift,
            'Beneficiary Bank SWIFT BIC',
            {
                allowAlphanumericCapital: true,
                allowLeadingZeros: true
            }
        );
        if (beneBankSwiftLength8Error && beneBankSwiftLength11Error) errors.BeneBankBic = 'Field must be exactly 8 or 11 characters';
        else if (beneBankSwiftFormatError) errors.BeneBankBic = beneBankSwiftFormatError;
    }

    // INFO: Beneficiary Info Section
    const beneInfo1 = values.BeneInfo1.trim();
    const beneInfo2 = values.BeneInfo2.trim();
    const beneInfo3 = values.BeneInfo3.trim();
    const beneInfo4 = values.BeneInfo4.trim();
    if (beneInfo1) {
        const beneInfo1FormatError = textFieldCharacterWhitelist(beneInfo1);
        const beneInfo1ExcelError = validateNoExcelFormulaStartingCharacters(beneInfo1);
        if (beneInfo1FormatError) errors.BeneInfo1 = beneInfo1FormatError;
        else if (beneInfo1ExcelError) errors.BeneInfo1 = beneInfo1ExcelError;
    }

    if (beneInfo2) {
        const beneInfo2FormatError = textFieldCharacterWhitelist(beneInfo2);
        const beneInfo2ExcelError = validateNoExcelFormulaStartingCharacters(beneInfo2);
        if (!beneInfo1) errors.BeneInfo2 = 'Beneficiary Information 1 must be populated first';
        else if (beneInfo2FormatError) errors.BeneInfo2 = beneInfo2FormatError;
        else if (beneInfo2ExcelError) errors.BeneInfo2 = beneInfo2ExcelError;
    }

    if (beneInfo3) {
        const beneInfo3FormatError = textFieldCharacterWhitelist(beneInfo3);
        const beneInfo3ExcelError = validateNoExcelFormulaStartingCharacters(beneInfo3);
        if (!beneInfo1 || !beneInfo2) errors.BeneInfo3 = 'Beneficiary Information 1 and 2 must be populated first';
        else if (beneInfo3FormatError) errors.BeneInfo3 = beneInfo3FormatError;
        else if (beneInfo3ExcelError) errors.BeneInfo3 = beneInfo3ExcelError;
    }

    if (beneInfo4) {
        const beneInfo4FormatError = textFieldCharacterWhitelist(beneInfo4);
        const beneInfo4ExcelError = validateNoExcelFormulaStartingCharacters(beneInfo4);
        if (!beneInfo1 || !beneInfo2 || !beneInfo3) errors.BeneInfo4 = 'Beneficiary Information 1, 2 and 3 must be populated first';
        else if (beneInfo4FormatError) errors.BeneInfo4 = beneInfo4FormatError;
        else if (beneInfo4ExcelError) errors.BeneInfo4 = beneInfo4ExcelError;
    }

    // INFO: Bank to Bank Info Section
    const bankToBankInfo1 = values.BankToBankInfo1.trim();
    const bankToBankInfo2 = values.BankToBankInfo2.trim();
    const bankToBankInfo3 = values.BankToBankInfo3.trim();
    const bankToBankInfo4 = values.BankToBankInfo4.trim();
    if (bankToBankInfo1) {
        const bankToBank1FormatError = textFieldCharacterWhitelist(bankToBankInfo1);
        const bankToBank1ExcelError = validateNoExcelFormulaStartingCharacters(bankToBankInfo1);
        if (bankToBank1FormatError) errors.BankToBankInfo1 = bankToBank1FormatError;
        else if (bankToBank1ExcelError) errors.BankToBankInfo1 = bankToBank1ExcelError;
    }

    if (bankToBankInfo2) {
        const bankToBank2FormatError = textFieldCharacterWhitelist(bankToBankInfo2);
        const bankToBank2ExcelError = validateNoExcelFormulaStartingCharacters(bankToBankInfo2);
        if (!bankToBankInfo1) errors.BankToBankInfo2 = 'Bank to Bank Information 1 must be populated first';
        else if (bankToBank2FormatError) errors.BankToBankInfo2 = bankToBank2FormatError;
        else if (bankToBank2ExcelError) errors.BankToBankInfo2 = bankToBank2ExcelError;
    }

    if (bankToBankInfo3) {
        const bankToBank3FormatError = textFieldCharacterWhitelist(bankToBankInfo3);
        const bankToBank3ExcelError = validateNoExcelFormulaStartingCharacters(bankToBankInfo3);
        if (!bankToBankInfo1 || !bankToBankInfo2) errors.BankToBankInfo3 = 'Bank to Bank Information 1 and 2 must be populated first';
        else if (bankToBank3FormatError) errors.BankToBankInfo3 = bankToBank3FormatError;
        else if (bankToBank3ExcelError) errors.BankToBankInfo3 = bankToBank3ExcelError;
    }

    if (bankToBankInfo4) {
        const bankToBank4FormatError = textFieldCharacterWhitelist(bankToBankInfo4);
        const bankToBank4ExcelError = validateNoExcelFormulaStartingCharacters(bankToBankInfo4);
        if (!bankToBankInfo1 || !bankToBankInfo2 || !bankToBankInfo3) errors.BankToBankInfo4 = 'Bank to Bank Information 1, 2 and 3 must be populated first';
        else if (bankToBank4FormatError) errors.BankToBankInfo4 = bankToBank4FormatError;
        else if (bankToBank4ExcelError) errors.BankToBankInfo4 = bankToBank4ExcelError;
    }
};
type AddressField = {
    fieldName: string;
    value: string;
};

type AddressFields = {
    streetAddress: AddressField;
    city: AddressField;
    stateProv: AddressField;
    postalCode: AddressField;
    countryCode: AddressField;
};

const validateAddress = (
    fields: AddressFields,
    errors: IErrors,
    domestic: boolean
) => {
    const {
        streetAddress,
        city,
        stateProv,
        postalCode,
        countryCode
    } = fields;

    const countryFormatError = validateCapitalLetters(countryCode.value);
    const countryLengthError = validateLengthRequirement(countryCode.value, 2, true);
    // INFO: Capital letter check will also confirm no PO Box, whitelist, and excel check since only A-Z allowed
    if (!countryCode.value) errors[countryCode.fieldName] = 'Required';
    else if (countryLengthError) errors[countryCode.fieldName] = countryLengthError;
    else if (countryFormatError) errors[countryCode.fieldName] = countryFormatError;

    const streetWhitelistError = textFieldCharacterWhitelist(streetAddress.value);
    const streetExcelError = validateNoExcelFormulaStartingCharacters(streetAddress.value);
    const streetPoBoxError = validateNoPoBox(streetAddress.value);
    if (!streetAddress.value) errors[streetAddress.fieldName] = 'Required';
    else if (streetWhitelistError) errors[streetAddress.fieldName] = streetWhitelistError;
    else if (streetExcelError) errors[streetAddress.fieldName] = streetExcelError;
    else if (streetPoBoxError) errors[streetAddress.fieldName] = streetPoBoxError;

    const cityWhitelistError = textFieldCharacterWhitelist(city.value, { excludeComma: true });
    const cityExcelError = validateNoExcelFormulaStartingCharacters(city.value);
    const cityPoBoxError = validateNoPoBox(city.value);
    if (!city.value) errors[city.fieldName] = 'Required';
    else if (cityWhitelistError) errors[city.fieldName] = cityWhitelistError;
    else if (cityExcelError) errors[city.fieldName] = cityExcelError;
    else if (cityPoBoxError) errors[city.fieldName] = cityPoBoxError;

    const postalWhitelistError = textFieldCharacterWhitelist(postalCode.value, { excludeComma: true });
    const postalExcelError = validateNoExcelFormulaStartingCharacters(postalCode.value);
    const postalPoBoxError = validateNoPoBox(postalCode.value);
    if (postalWhitelistError) errors[postalCode.fieldName] = postalWhitelistError;
    else if (postalExcelError) errors[postalCode.fieldName] = postalExcelError;
    else if (postalPoBoxError) errors[postalCode.fieldName] = postalPoBoxError;

    if (domestic) {
        if (!stateProv.value) errors[stateProv.fieldName] = 'Required';
    } else {
        const stateProvWhitelistError = textFieldCharacterWhitelist(stateProv.value, { excludeComma: true });
        const stateProvExcelError = validateNoExcelFormulaStartingCharacters(stateProv.value);
        const stateProvPoBoxError = validateNoPoBox(stateProv.value);
        if (stateProvWhitelistError) errors[stateProv.fieldName] = stateProvWhitelistError;
        else if (stateProvExcelError) errors[stateProv.fieldName] = stateProvExcelError;
        else if (stateProvPoBoxError) errors[stateProv.fieldName] = stateProvPoBoxError;

        if (countryCode.value === 'US') errors[countryCode.fieldName] = 'Country code cannot be US';
    }
};